// eslint-disable-next-line monorepo/forbidden-imports
import type { MeMenuNavigationConfig } from '@xing-com/top-bar';

import type { ConfigurationItem, Language, Translate } from '../types';

const NAVIGATION_BADGE_NO_BADGE = 'navigation_badge_no_badge';

export const meMenuNavigation: ConfigurationItem = (
  t: Translate,
  lang: Language
): MeMenuNavigationConfig => {
  return [
    [
      'Me',
      {
        href: '/profile',
        isOnMeMenu: true,
        hasProfileProgressIndicator: false,
        tracking: {
          sc_o: 'navigation_profile_icon',
          sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
        },
      },
    ],
    ['Spacer', { height: '3XL' }],
    [
      'MeMenuItem',
      {
        link: {
          href: '/profile',
          title: t('MEMENU_PROFILE_LABEL'),
          hasProfileProgressIndicator: true,
          'data-testid': 'me-menu-item-profile',
          tracking: {
            sc_o: 'navigation_profile',
          },
        },
      },
    ],
    [
      'MeMenuItem',
      {
        link: {
          href: '/jobseeker-criteria',
          title: t('MEMENU_JOB_PREFERENCES_LABEL'),
          'data-testid': 'me-menu-item-jobseeker-criteria',
          tracking: {
            sc_o: 'navigation_job_preferences',
            sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
          },
        },
      },
    ],
    [
      'MeMenuItem',
      {
        link: {
          href: '/news/selections/following',
          title: t('MEMENU_FOLLOWING_LABEL'),
          'data-testid': 'me-menu-item-following',
          tracking: {
            sc_o: 'navigation_following',
            sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
          },
        },
      },
    ],
    [
      'MeMenuItem',
      {
        link: {
          href: '/discover/your-posts',
          title: t('MEMENU_MY_POSTS_LABEL'),
          'data-testid': 'me-menu-item-your-posts',
          tracking: {
            sc_o: 'navigation_my_posts',
            sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
          },
        },
      },
    ],
    ['Spacer', {}],
    [
      'FeatureCondition',
      {
        children: {
          $meta: [
            [
              'MeMenuItem',
              {
                link: {
                  title: t('MEMENU_PREMIUM_LABEL'),
                  href: '/premium',
                  color: 'premium',
                  'data-testid': 'me-menu-item-premium',
                  tracking: {
                    sc_o: 'navigation_premium',
                    sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                  },
                },
              },
            ],
          ],
        },
        elseCase: {
          $meta: [
            [
              'MeMenuItem',
              {
                link: {
                  activeItemValues: ['premium'],
                  title: t('MEMENU_PREMIUM_FOR_BASIC_USER_LABEL'),
                  href: '/purchase/premium?reagent=uplt_1014',
                  color: 'premium',
                  'data-testid': 'me-menu-item-premium-upsell',
                  tracking: {
                    sc_o: 'navigation_premium',
                    sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                  },
                },
              },
            ],
          ],
        },
        flags: 'isPremium',
      },
    ],
    [
      'FeatureCondition',
      {
        children: {
          $meta: [
            [
              'MeMenuItem',
              {
                link: {
                  title: t('MEMENU_PROJOBS_LABEL'),
                  href: '/projobs',
                  color: 'proJobs',
                  'data-testid': 'me-menu-item-projobs',
                  tracking: {
                    sc_o: 'navigation_projobs',
                    sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                  },
                },
              },
            ],
          ],
        },
        elseCase: {
          $meta: [
            [
              'MeMenuItem',
              {
                link: {
                  color: 'proJobs',
                  title: t('MEMENU_PROJOBS_FOR_BASIC_USER_LABEL'),
                  href: '/purchase/projobs?reagent=uplt_1015',
                  'data-testid': 'me-menu-item-projobs-upsell',
                  tracking: {
                    sc_o: 'navigation_projobs',
                    sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                  },
                },
              },
            ],
          ],
        },
        flags: 'hasProJobsMembership',
      },
    ],
    [
      'FeatureCondition',
      {
        children: {
          $meta: [
            [
              'MeMenuItem',
              {
                link: {
                  title: t('MEMENU_PROBUSINESS_LABEL'),
                  href: '/probusiness',
                  color: 'proBusiness',
                  'data-testid': 'me-menu-item-probusiness',
                  tracking: {
                    sc_o: 'navigation_probusiness',
                    sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                  },
                },
              },
            ],
          ],
        },
        flags: 'showProbusinessInNavigation',
      },
    ],
    ['Spacer', {}],
    [
      'FeatureCondition',
      {
        children: {
          $meta: [
            [
              'MeMenuItem',
              {
                link: {
                  title: t('MEMENU_JOB_ADS_LABEL'),
                  href: '/jobmanager/online',
                  'data-testid': 'me-menu-item-job-ads',
                  tracking: {
                    sc_o: 'navigation_manage_jobs_ads',
                    sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                  },
                },
              },
            ],
          ],
        },
        flags: 'isJobsPoster',
      },
    ],
    [
      'MeMenuItem',
      {
        link: {
          href: '/settings',
          title: t('MEMENU_SETTINGS_LABEL'),
          'data-testid': 'me-menu-item-settings',
          tracking: {
            sc_o: 'navigation_settings',
            sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
          },
        },
      },
    ],
    [
      'MeMenuItem',
      {
        link: {
          href: `https://faq.xing.com/${lang}`,
          title: t('MEMENU_HELP_LABEL'),
          'data-testid': 'me-menu-item-help',
          tracking: {
            sc_o: 'navigation_help',
            sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
          },
        },
      },
    ],
  ];
};
